import { Box, Flex, Text } from '@modusign/design-system';
import { LabelWithBookmarkResponse } from '@modusign/openapi-client-category/generated/model';

import IconLabel from '@app/features/label/components/IconLabel';
import { useLabelChangeContext } from '@app/features/label/components/LabelPopovers/context/LabelChanges';
import { LabelsLabelingState } from '@app/features/label/components/LabelPopovers/context/types';
import LabelCheckListItem from '@app/features/label/components/LabelPopovers/LabelApplyPopover/LabelCheckList/LabelCheckListItem';
import { LabelFilters } from '@app/features/label/types';
import { MAX_COUNT_OF_LABELS_PER_ONE_TIME_APPLY } from '@app/features/workspaceManagement/constants';

import LabelListItem from '../../components/LabelListItem';

interface LabelInfiniteCheckListProps {
  initialLabeled: LabelsLabelingState;
  labels: LabelWithBookmarkResponse[];
  filters: LabelFilters;
}

function LabelInfiniteCheckList({ initialLabeled, labels, filters }: LabelInfiniteCheckListProps) {
  const { labelChangesMap, add, remove, hasExceedChangeLimit } = useLabelChangeContext();

  const sorted = labels.sort((a, b) => {
    const isALabeled = !!initialLabeled?.[a.id];
    const isBLabeled = !!initialLabeled?.[b.id];

    if (isALabeled === isBLabeled) {
      return 0;
    }
    return isALabeled ? -1 : 1;
  });

  return (
    <Flex as="section" flexDirection="column" maxHeight="300px" overflowY="scroll" py={4}>
      {sorted.map((label) => (
        <LabelListItem active={!!labelChangesMap.get(label.id)} key={label.id} label={label}>
          <LabelCheckListItem
            checked={labelChangesMap.get(label.id) ?? initialLabeled[label.id] ?? 'NONE'}
            disableMessage={
              !labelChangesMap.get(label.id) &&
              hasExceedChangeLimit &&
              `한 번에 ${MAX_COUNT_OF_LABELS_PER_ONE_TIME_APPLY}개의 라벨만 변경할 수 있습니다.`
            }
            onCheck={(target) => {
              const isDifferFromInitial = (initialLabeled[label.id] || 'NONE') !== target;
              // 해당 라벨의 초기 체크상태 또는 NONE과 비교해서, 변화가 있다면 add, 아니면 remove
              if (!isDifferFromInitial) {
                remove(label.id);
              }

              if (isDifferFromInitial && target !== 'SOME') {
                add(label.id, target);
              }
            }}
          >
            <Box width="100%">
              <IconLabel bookmarked={!!label.bookmark} name={`${label.name}`} />
            </Box>
          </LabelCheckListItem>
        </LabelListItem>
      ))}
      {labels.length === 0 && (
        <Text m={4} textAlign="center">
          {filters?.name ? (
            <>
              <Text fontWeight="medium">{`'${filters?.name}' `}</Text>
              검색 결과가 없습니다.
            </>
          ) : (
            '새 라벨을 추가해 문서를 분류해 보세요.'
          )}
        </Text>
      )}
    </Flex>
  );
}

export default LabelInfiniteCheckList;
